<script setup>
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const props = defineProps({
    node: {
      type: Object,
      required: true
    },
    urlKey: {
      type: String,
      required: true
    }
  });

  const selectedCodes = defineModel('selectedCodes', {
    type: Array,
    required: true,
    default: () => []
  });

  const children = computed(() => props.node.children || []);
  const hasChildren = computed(() => children.value.length > 0);

  const nodeId = computed(() => `${props.urlKey}_${props.node.code}`);

  const containsSelection = (node) => (
    selectedCodes.value.includes(node.code) || (node.children || []).some(containsSelection)
  );

  const checked = computed(() => selectedCodes.value.includes(props.node.code));
  const hasSelectedDescendant = computed(() => children.value.some(containsSelection));

  // Unchecked node holding a selection somewhere below it: show it as partially
  // selected. `indeterminate` only exists as a DOM property, hence the `.prop`
  // binding in the template.
  const indeterminate = computed(() => !checked.value && hasSelectedDescendant.value);

  // Start expanded when the selection lives somewhere below this node.
  const expanded = ref(hasSelectedDescendant.value);

  const toggleExpanded = () => {
    expanded.value = !expanded.value;
  };
</script>

<template>
  <li class="classification-tree__node">
    <div class="d-flex align-items-start mb-2">
      <button
        v-if="hasChildren"
        class="btn btn-link btn-sm p-0 classification-tree__toggle"
        type="button"
        :aria-expanded="expanded"
        :aria-controls="`${nodeId}_children`"
        :aria-label="expanded ? t('components.classification_tree.collapse', { label: node.label }) : t('components.classification_tree.expand', { label: node.label })"
        @click="toggleExpanded"
      >
        <i :class="expanded ? 'bi bi-caret-down-fill' : 'bi bi-caret-right-fill'" aria-hidden="true"></i>
      </button>
      <span v-else class="classification-tree__toggle" aria-hidden="true"></span>
      <div class="form-check">
        <input
          :id="nodeId"
          v-model="selectedCodes"
          class="form-check-input"
          type="checkbox"
          :name="urlKey"
          :value="node.code"
          :indeterminate.prop="indeterminate"
        >
        <label class="form-check-label" :for="nodeId">{{ node.label }}</label>
      </div>
    </div>
    <ul
      v-if="hasChildren && expanded"
      :id="`${nodeId}_children`"
      class="list-unstyled ps-3 mb-0"
    >
      <ClassificationTreeNode
        v-for="child in children"
        :key="child.code"
        v-model:selected-codes="selectedCodes"
        :node="child"
        :url-key="urlKey"
      />
    </ul>
  </li>
</template>

<style scoped lang="scss">
  // Same box whether the node is expandable or not, so every checkbox of a
  // given depth lines up. The `!important` overrides the global
  // `button { margin-left: 12px !important }` of catalog_publication.scss.
  .classification-tree__toggle {
    display: inline-block;
    flex: 0 0 1rem;
    width: 1rem;
    margin: 0 0.25rem 0 0 !important;
    line-height: 1.5;
    text-align: center;
    text-decoration: none;
  }
</style>
